/**
 * Binance P2C/C2C Adapter — normalizes all Binance API calls to the domain model.
 * Preserves 100% of existing behavior: host rotation, CORS proxy, fake ads filter, VIP styling.
 */

import axios from "axios";
import { ExchangeAdapter } from "./types";
import {
  P2PAd,
  PriceSignal,
  SearchParams,
  SearchResult,
  P2PAdDetail,
} from "./domain";

// ─── Host pools (anti-rate-limit rotation) ──────────────────────────────
const SEARCH_HOSTS = [
  "web3.binance.info",
  "web3.binance.com",
  // "developers.binance.info",
  // "c2c-admin.binance.info",
  "c2c.binance.com",
  // "c2c.binance.info",
  "p2p.binance.com",
  // "p2p.binance.info",
  "www.binance.com",
  // "www.binance.info",
];

const DETAIL_HOSTS = ["p2p.binance.com", "c2c.binance.com"];

const PHP_PROXIES = [
  "https://freehost-2.lehongthai.vip/cors-anywhere.php",
  "https://host-1.lehongthai.vip/cors-anywhere.php",
];

// ─── VIP merchant rules (Binance-specific) ──────────────────────────────
interface VipRule {
  match: (ad: P2PAd) => boolean;
  className: string;
  /** Minimum accessLevel to see this rule. Default: 1 (premium+admin). Set 0 for free users, 2 for admin-only. */
  minAccessLevel?: number;
}

const VIP_RULES: VipRule[] = [
  // Block list (orange/red)
  {
    match: (ad) =>
      ["VietNam_Pro_P2P", "Trending_VIP", "Shin_Pro_USDT"].some((k) =>
        ad.advertiser.nickName.includes(k),
      ),
    className: "bg-orange-300 !text-red-600 opacity-25",
    minAccessLevel: 2,
  },
  {
    match: (ad) => ad.advertiser.nickName.includes("AutoPay_24h_VIP"),
    className: "bg-red-300 !text-orange-700 opacity-50",
    minAccessLevel: 2,
  },
  {
    match: (ad) =>
      [
        "Supermarket_Crypto",
        "QuickPay_Express_VIP",
        "Automated trading",
        "Merchant_Autopay_VN",
        "PhamNgan_P2P_VietNam",
        "AUTOpay_Merchant_VN",
        "AUTO_Promax_VIP",
        "THEGIOI_USDT_24H",
      ].some((k) => ad.advertiser.nickName.includes(k)),
    className: "bg-red-300 !text-stone-600 opacity-30",
    minAccessLevel: 2,
  },

  // Đổi giá team (stone/yellow)
  {
    match: (ad) =>
      [
        "VIP_Auto247",
        "HNhat_OTC_247",
        "GDnhanh60s",
        "GDNhanh-UyTinh247",
        "SafeTrade_CryptoVN",
        "VNxTrustDesk",
        "GiaoDịch_30S",
      ].some((k) => ad.advertiser.nickName.includes(k)),
    className: "!bg-stone-600 !text-yellow-400 opacity-30",
  },

  // Cyan group (premium)
  {
    match: (ad) =>
      ["VipTraderPRO", "KennyPhanVIP", "GDNhanh30s", "GiaoDichUyTin-247", 'DinoP', 'Kanono247'].some(
        (k) => ad.advertiser.nickName.includes(k),
      ),
    className: "bg-cyan-800 text-white-800 opacity-50",
    minAccessLevel: 1,
  },

  // Rose group (main VIP list)
  {
    match: (ad) =>
      [
        "Cu-lun",
        "Jogker",
        "Le_Tieu",
        "HOANG_USDT",
        "HOANG_BTC-247",
        "DucDH",
        "MaiHuyen24h",
        "BachDuy_29",
        "p0Lo1101",
        "Trouday-BNB",
        "BuFFaLo-17",
        "MadMAX_OTC",
        "OTC_Ha-Noi_0972968000",
        "MuaBanUSDT-VND",
        "HuyHoang_ExChange365",
        "AE_LamSan_0858563563",
        "LunCT0411",
        "T3Crypto",
        "PhamLink",
        "Em_Xinhh",
        "RichHappyEnergyX999999999",
        "SuperP2P_GDNhanh247",
        "QuynhTran1988",
        "Pan_Crypto",
        "VuDu_OTC-247",
        "GIANG_OTC-247",
        "VS_Capital6886",
        "BrokenFang",
        "Nam-CT",
        "420vietnam",
        "Haru_Aki",
        "NaNi_SuperP2P",
        "Donal C",
        "Hằng ChuPaPiPoNhaNho",
        "Truong_Tino",
        "BaoAn-BNB",
        "Cuong_Felix",
        "RONG_OTC-247",
        "Huytamsix_P2P_247",
        "HIẾU-BTC-247",
        "SwiftTrade_GDNhanhP2P",
        "STerry_15s",
        "BaoBoi_Cypto365",
        "Jinkyyyy",
        "NguyenDu_OTC-247",
        "Az_VoiCon",
        "Ricco_86",
        "CauUt_Parrot8386",
        "KIENKAO-9999",
        "Atlatvn",
        "Nambtc_886",
        "Drew_crypto_1102",
        "TomNguyen_BTC-247",
        "Slay Crypto",
        "Fancyyy_BNB",
        "ToanDuong_P2P",
        "LONG_BTC_8888",
        "DuyTri_OTC-247",
        "HT_Warrior",
        "Dolce_Gabbana_79",
        "VAS-OTC",
        "Mocoivo1995",
        "HanhTranOTC",
        "AnhQuan_24h",
        "Anh Vũ_ 24h",
        "VietDung-96",
        "Sua_P2P",
        "CTteamPro",
        "Evergreen_VN",
        "Smoothies_3112",
        "VIET_BTC-247",
        "KhuongDuy_KDA",
        "P2P-GiaodichThanToc",
        "HattDauXanh",
        "NTQKBitcoinGoat",
        "Kippi_kendy",
        "SongLam-NuiHong-Otc",
        "slaycrypto",
        "Hieu_Ryan",
        "-Stewie-",
        "TuanPB",
        "Ice-CreamP2P",
        "Van_Heng2104",
        "yellow",
        "MaiMon_99",
        "KevinBui_99999",
        "Miu_Smile_FastP2P",
        "Chito68",
        "USDT_Ha_Noi_TpHCM_15",
        "SJC_9999",
        "happyland86",
        "Horse-17",
        "TramBui_68",
        "Trung-CT",
        "Tungbilly99",
        "Dreamer97666-CT",
        "Anhnguyen1978",
        "M_Lucky_Crypto",
      ].some((k) => ad.advertiser.nickName.includes(k)),
    className: "bg-rose-800 text-white-800 opacity-100",
    minAccessLevel: 1,
  },

  // Animated VIP (indigo/purple/teal)
  {
    match: (ad) => ad.advertiser.nickName.includes("BendTheTrend x HỏaTốc247"),
    className:
      "bg-indigo-600 !text-white not-italic relative overflow-hidden hover:bg-indigo-700 transition-all duration-300",
    minAccessLevel: 2,
  },
  {
    match: (ad) =>
      [
        "GiaoDichVIP_247",
        "GiaoNgay_247",
        "VuaTocDo_VIP",
        "GiaoNhanh_TuDong_247",
      ].some((k) => ad.advertiser.nickName.includes(k)),
    className:
      "bg-teal-400 !text-white not-italic relative overflow-hidden hover:bg-teal-700 transition-all duration-300",
    minAccessLevel: 2,
  },
  {
    match: (ad) => ad.advertiser.nickName.includes("ThanhToanTuDong_247"),
    className:
      "bg-violet-800 !text-white not-italic relative overflow-hidden hover:bg-indigo-700 transition-all duration-300",
    minAccessLevel: 2,
  },
  {
    match: (ad) => ad.advertiser.nickName.includes("ThuongGiaTienSo_VIP"),
    className:
      "bg-teal-600 !text-white not-italic relative overflow-hidden hover:bg-teal-700 transition-all duration-300",
    minAccessLevel: 2,
  },
  {
    match: (ad) => ad.advertiser.nickName.includes("GiaoDichNhanh_60s"),
    className:
      "bg-lime-600 !text-white not-italic relative overflow-hidden hover:bg-lime-700 transition-all duration-300",
    minAccessLevel: 2,
  },
  {
    match: (ad) => ad.advertiser.nickName.includes("VIP-GiaoDichNhanh"),
    className:
      "bg-amber-600 !text-white not-italic relative overflow-hidden hover:bg-amber-700 transition-all duration-300",
    minAccessLevel: 2,
  },
  {
    match: (ad) =>
      ["ShopUSDT_247", "TổngKhoUSDT_247"].some((k) =>
        ad.advertiser.nickName.includes(k),
      ),
    className:
      "bg-rose-600 !text-white not-italic relative overflow-hidden hover:bg-amber-700 transition-all duration-300",
    minAccessLevel: 2,
  },

  // Rose special
  {
    match: (ad) =>
      ["Butdeath270501", "Pro_VIP_Trader"].some((k) =>
        ad.advertiser.nickName.includes(k),
      ),
    className: "bg-rose-600 !text-white",
  },

  // Binance promotion zone (privilegeType === 8, admin-only)
  {
    match: (ad) => !!ad.promotionZone,
    className:
      "!text-white !bg-slate-600 !opacity-100 relative before:absolute before:inset-0 before:border-2 before:border-yellow-400 before:rounded-md before:animate-[pulse_2s_ease-in-out_infinite]",
    minAccessLevel: 0,
  },
];

// ─── Helper: pick random host ────────────────────────────────────────────
function randomHost<T>(hosts: T[]): T {
  return hosts[Math.floor(Math.random() * hosts.length)];
}

// ─── Helper: detect mobile from User-Agent (for CORS proxy) ─────────────
function isMobileFromUA(): boolean {
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
}

// ─── Normalize tradeType: Binance 0=SELL, 1=BUY ────────────────────────
function normalizeTradeType(raw: number | string): "BUY" | "SELL" {
  const val = typeof raw === "string" ? parseInt(raw, 10) : raw;
  if (val === 1 || raw === "BUY") return "BUY";
  return "SELL";
}

// ─── Normalize advertiser type ──────────────────────────────────────────
function normalizeUserType(rawData: {
  userType?: string;
  userIdentity?: string;
}): "user" | "merchant" {
  if (rawData.userType === "merchant") return "merchant";
  if (
    rawData.userIdentity === "MASS_MERCHANT" ||
    rawData.userIdentity === "PRO_MERCHANT"
  )
    return "merchant";
  return "user";
}

// ─── Normalize a search result item to P2PAd ────────────────────────────
function normalizeAd(item: any): P2PAd {
  const adv = item.adv || {};
  const advertiser = item.advertiser || {};

  return {
    advNo: adv.advNo ?? "",
    tradeType: normalizeTradeType(adv.tradeType),
    asset: adv.asset ?? "USDT",
    price: String(adv.price ?? ""),
    surplusAmount: Number(adv.surplusAmount ?? 0),
    tradableQuantity: String(adv.tradableQuantity ?? ""),
    maxSingleTransAmount: Number(adv.maxSingleTransAmount ?? 0),
    minSingleTransAmount: Number(adv.minSingleTransAmount ?? 0),
    dynamicMaxSingleTransAmount: Number(adv.dynamicMaxSingleTransAmount ?? 0),
    commissionRate: Number(adv.commissionRate ?? 0),
    payTimeLimit: Number(adv.payTimeLimit ?? 15),
    classify: adv.classify,
    promotionZone: item.privilegeType === 8,
    takerAdditionalKycRequired: adv.takerAdditionalKycRequired ?? 0,
    tradeMethods: (adv.tradeMethods || []).map((m: any) => ({
      payId: m.payId ?? null,
      payMethodId: m.payMethodId ?? "",
      payType: m.payType ?? "",
      payAccount: m.payAccount ?? null,
      payBank: m.payBank ?? null,
      paySubBank: m.paySubBank ?? null,
      identifier: m.identifier ?? "",
      tradeMethodName: m.tradeMethodName ?? m.tradeMethodShortName ?? "",
      tradeMethodShortName: m.tradeMethodShortName ?? "",
      tradeMethodBgColor: m.tradeMethodBgColor ?? "",
      iconUrl: m.iconUrlColor ?? "",
    })),
    advertiser: {
      userNo: advertiser.userNo ?? "",
      nickName: advertiser.nickName ?? "",
      userType: normalizeUserType(advertiser),
      monthFinishRate: Number(advertiser.monthFinishRate ?? 0),
      monthOrderCount: Number(advertiser.monthOrderCount ?? 0),
      positiveRate: Number(advertiser.positiveRate ?? 0),
      activeTimeInSecond: Number(advertiser.activeTimeInSecond ?? 9999),
      vipLevel: Number(advertiser.vipLevel ?? 0),
    },
    assetScale: adv.assetScale,
    buyerKycLimit: adv.buyerKycLimit,
  };
}

// ─── Fake ads detection (Binance-specific heuristics) ───────────────────
function isFakeAds(ad: P2PAd): boolean {
  const dynamicMaxTrade = ad.dynamicMaxSingleTransAmount;
  const percentSuccess = (ad.advertiser.monthFinishRate ?? 0) * 100;
  const minTrade = ad.minSingleTransAmount;

  // Only MoMo → fake
  if (ad.tradeMethods.every((m) => m.payType === "MoMo")) return true;
  if (dynamicMaxTrade <= 5_000_000) return true;
  if (percentSuccess < 90) return true;

  // High min + low success + huge max → fake
  if (
    minTrade >= 50_000_000 &&
    percentSuccess < 97 &&
    dynamicMaxTrade >= 1_000_000_000
  )
    return true;

  // Low ratio + low max or success
  if (
    dynamicMaxTrade / minTrade < 3 &&
    (dynamicMaxTrade < 8_000_000 || percentSuccess < 98)
  )
    return true;

  return false;
}

// ─── Price signals (emoji indicators — 100% original thresholds) ────────
function getPriceSignals(
  ad: P2PAd,
  accessLevel: number,
  ctx?: {
    isKycRequired?: boolean;
    hasBankPaymentMethod?: boolean;
    transAmount?: number;
  },
): PriceSignal[] {
  const signals: PriceSignal[] = [];
  const {
    dynamicMaxSingleTransAmount,
    tradeType,
    classify,
    minSingleTransAmount,
    maxSingleTransAmount,
    takerAdditionalKycRequired,
  } = ad;
  const isKyc = ctx?.isKycRequired ?? takerAdditionalKycRequired === 1;
  const hasBank = ctx?.hasBankPaymentMethod ?? true;
  const transAmt = ctx?.transAmount ?? 0;

  // 🐳 cá mập: SELL 500M-1B
  if (
    accessLevel >= 1 &&
    dynamicMaxSingleTransAmount >= 500_000_000 &&
    dynamicMaxSingleTransAmount < 1_000_000_000 &&
    tradeType === "SELL"
  ) {
    signals.push({ emoji: "🐳", label: "Cá mập (bán)", priceClass: "" });
  }

  // 🐋 cá ông: SELL ≥1B (không block)
  if (
    accessLevel >= 1 &&
    dynamicMaxSingleTransAmount >= 1_000_000_000 &&
    tradeType === "SELL" &&
    classify !== "block"
  ) {
    signals.push({ emoji: "🐋", label: "Cá ông (bán)", priceClass: "" });
  }

  // 🐬 cá heo: BUY ≥1B (không block) — cho free & premium
  if (
    accessLevel >= 1 &&
    dynamicMaxSingleTransAmount >= 1_000_000_000 &&
    tradeType === "BUY" &&
    classify !== "block"
  ) {
    signals.push({ emoji: "🐬", label: "Cá heo (mua)", priceClass: "" });
  }

  // 💠 block premium: access ≥1, classify block
  if (accessLevel >= 1 && classify === "block") {
    signals.push({ emoji: "💠", label: "Block premium", priceClass: "" });
  }

  // 🐬 cá heo: access 0, classify block
  if (accessLevel >= 1 && classify === "block") {
    signals.push({ emoji: "🐬", label: "Block (free)", priceClass: "" });
  }

  // ⚠️ cảnh báo min cao: access 0, SELL, min 99M-700M
  if (
    accessLevel >= 1 &&
    tradeType === "SELL" &&
    minSingleTransAmount >= 99_000_000 &&
    minSingleTransAmount < 700_000_000
  ) {
    signals.push({ emoji: "⚠️", label: "Min cao (≥99M)", priceClass: "" });
  }

  // 🛡️ KYC required
  if (accessLevel >= 1 && isKyc) {
    signals.push({ emoji: "🛡️", label: "Yêu cầu KYC", priceClass: "" });
  }

  // 💀 không có BANK
  if (accessLevel >= 1 && !hasBank) {
    signals.push({ emoji: "💀", label: "Không có BANK", priceClass: "" });
  }

  // 🐲 rồng: BUY, min 19M-60M, max >1B, trans ≥100M — access ≥1
  if (
    accessLevel >= 1 &&
    tradeType === "BUY" &&
    minSingleTransAmount > 19_000_000 &&
    minSingleTransAmount < 60_000_000 &&
    maxSingleTransAmount > 1_000_000_000 &&
    transAmt >= 100_000_000
  ) {
    signals.push({ emoji: "🐲", label: "Rồng (mua signaled)", priceClass: "" });
  }

  return signals;
}

// ─── Promotion zone style lookup ────────────────────────────────────────
function getPromotionZoneStyle(ad: P2PAd, accessLevel: number): string | null {
  for (const rule of VIP_RULES) {
    if (accessLevel < (rule.minAccessLevel ?? 1)) continue;
    if (rule.match(ad)) return rule.className;
  }

  return null;
}

// ══════════════════════════════════════════════════════════════════════
//  Binance Adapter Implementation
// ══════════════════════════════════════════════════════════════════════
export const binanceAdapter: ExchangeAdapter = {
  id: "binance",
  displayName: "Binance P2P",
  title: "Binance P2P",
  favicon: "/favicon.ico",

  // ─── searchAds (fetchPro) ───────────────────────────────────────
  async searchAds(
    params: SearchParams,
    isMobile?: boolean,
  ): Promise<SearchResult> {
    const mobile = isMobile ?? isMobileFromUA();

    const payTypes: string[] = [];

    const body = JSON.stringify({
      page: params.page,
      rows: params.rows,
      payTypes,
      asset: params.asset,
      tradeType: params.tradeType === 0 ? "SELL" : "BUY",
      fiat: "VND",
      filterType: "tradable",
      additionalKycVerifyFilter: 0,
      publisherType: params.isMerchantOnly ? "merchant" : null,
      classifies: ["mass", "profession", "fiat_trade"],
      transAmount: params.transAmount,
      merchantCheck: false,
    });

    const host = randomHost(SEARCH_HOSTS);
    const targetApiUrl = `https://${host}/bapi/c2c/v2/friendly/c2c/adv/search`;

    const config = mobile
      ? {
          method: "post" as const,
          url: `${randomHost(PHP_PROXIES)}?url=${encodeURIComponent(targetApiUrl)}`,
          headers: {
            Accept: "application/json, text/plain, */*",
            "Content-Type": "application/json",
            "Accept-Language": "en-GB,en-US;q=0.9,en;q=0.8",
          },
          data: body,
        }
      : {
          method: "post" as const,
          url: targetApiUrl,
          headers: {
            Accept: "application/json, text/plain, */*",
            "Content-Type": "application/json",
            "Accept-Language": "en-GB,en-US;q=0.9,en;q=0.8",
          },
          data: body,
        };

    let res: any;
    try {
      res = (await axios(config)).data;
    } catch (err: any) {
      // Re-throw 429 so the caller (PriceTable) can handle stale data
      if (err?.response?.status === 429) {
        throw err;
      }
      return { data: [] };
    }

    // Normalize to domain model
    const adsList = res?.data ?? [];
    const result: P2PAd[] = adsList.map((item: any) => normalizeAd(item));
    return { data: result };
  },

  // ─── getAdDetail (fetchAdvDetailByNo) ─────────────────────────────
  async getAdDetail(
    advNo: string,
    isMobile?: boolean,
  ): Promise<P2PAdDetail | null> {
    const mobile = isMobile ?? isMobileFromUA();

    const host = randomHost(DETAIL_HOSTS);
    const targetApiUrl = `https://${host}/bapi/c2c/v2/public/c2c/adv/detail-with-advertiser?advNo=${advNo}`;

    const config = mobile
      ? {
          method: "get" as const,
          url: `${randomHost(PHP_PROXIES)}?url=${encodeURIComponent(targetApiUrl)}`,
          headers: {
            Accept: "application/json, text/plain, */*",
            "Accept-Language": "en-GB,en-US;q=0.9,en;q=0.8",
          },
        }
      : {
          method: "get" as const,
          url: targetApiUrl,
          headers: {
            Accept: "application/json, text/plain, */*",
            "Accept-Language": "en-GB,en-US;q=0.9,en;q=0.8",
          },
        };

    const res = await axios(config)
      .then((r) => r.data)
      .catch((err) => err?.response?.data || null);

    if (!res || res.code !== "000000" || !res.data?.adv) return null;

    const raw = res.data;
    const adv = raw.adv || {};
    const advertiser = raw.advertiser || {};

    return {
      adv: {
        advNo: adv.advNo ?? "",
        tradeType: normalizeTradeType(adv.tradeType),
        asset: adv.asset ?? "USDT",
        price: String(adv.price ?? ""),
        surplusAmount: Number(adv.surplusAmount ?? 0),
        tradableQuantity: String(adv.tradableQuantity ?? ""),
        maxSingleTransAmount: Number(adv.maxSingleTransAmount ?? 0),
        minSingleTransAmount: Number(adv.minSingleTransAmount ?? 0),
        dynamicMaxSingleTransAmount: Number(
          adv.dynamicMaxSingleTransAmount ?? 0,
        ),
        commissionRate: Number(adv.commissionRate ?? 0),
        payTimeLimit: Number(adv.payTimeLimit ?? 15),
        classify: adv.classify,
        takerAdditionalKycRequired: adv.takerAdditionalKycRequired ?? 0,
        tradeMethods: (adv.tradeMethods || []).map((m: any) => ({
          payId: m.payId ?? null,
          payMethodId: m.payMethodId ?? "",
          payType: m.payType ?? "",
          payAccount: m.payAccount ?? null,
          payBank: m.payBank ?? null,
          paySubBank: m.paySubBank ?? null,
          identifier: m.identifier ?? "",
          tradeMethodName: m.tradeMethodName ?? m.payType ?? "",
          tradeMethodShortName: m.tradeMethodShortName ?? "",
          tradeMethodBgColor: m.tradeMethodBgColor ?? "",
          iconUrl: m.iconUrlColor ?? "",
        })),

        // Advanced fields
        initAmount: Number(adv.initAmount ?? 0),
        createTime: adv.createTime ?? 0,
        advUpdateTime: adv.advUpdateTime ?? 0,
        advStatus: adv.advStatus ?? 0,

        // Pricing
        priceType: adv.priceType ?? 0,
        priceFloatingRatio: adv.priceFloatingRatio ?? "",
        rateFloatingRatio: adv.rateFloatingRatio ?? "",
        currencyRate: adv.currencyRate ?? null,
        fiatUnit: adv.fiatUnit ?? "VND",
        fiatScale: adv.fiatScale ?? 0,
        priceScale: adv.priceScale ?? 0,
        fiatSymbol: adv.fiatSymbol ?? "",
        assetLogo: adv.assetLogo ?? "",

        // Quantity
        minSingleTransQuantity: adv.minSingleTransQuantity ?? "",
        maxSingleTransQuantity: adv.maxSingleTransQuantity ?? "",
        dynamicMaxSingleTransQuantity: adv.dynamicMaxSingleTransQuantity ?? "",

        // Limits
        allowTradeMerchant: Number(adv.allowTradeMerchant ?? 0),
        buyerRegDaysLimit: adv.buyerRegDaysLimit ?? -1,
        buyerBtcPositionLimit: Number(adv.buyerBtcPositionLimit ?? -1),

        // Content
        remarks: adv.remarks ?? "",
        abnormalStatusList: Array.isArray(adv.abnormalStatusList)
          ? adv.abnormalStatusList
          : [],
        autoReplyMsg: adv.autoReplyMsg ?? null,

        // Fee
        takerCommissionRate: adv.takerCommissionRate ?? null,
        minTakerFee: adv.minTakerFee ?? null,

        // Status
        isSafePayment: adv.isSafePayment ?? false,
        inventoryType: adv.inventoryType ?? 0,
        offlineReason: adv.offlineReason ?? "",
        closeReason: adv.closeReason ?? "",

        // Trade filters
        userTradeCountFilterTime: adv.userTradeCountFilterTime ?? 0,
        userBuyTradeCountMin: adv.userBuyTradeCountMin ?? 0,
        userBuyTradeCountMax: adv.userBuyTradeCountMax ?? 0,
        userSellTradeCountMin: adv.userSellTradeCountMin ?? 0,
        userSellTradeCountMax: adv.userSellTradeCountMax ?? 0,
        userAllTradeCountMin: adv.userAllTradeCountMin ?? 0,
        userAllTradeCountMax: adv.userAllTradeCountMax ?? 0,
        userTradeCompleteRateFilterTime:
          adv.userTradeCompleteRateFilterTime ?? null,
        userTradeCompleteCountMin: adv.userTradeCompleteCountMin ?? 0,
        userTradeCompleteRateMin: adv.userTradeCompleteRateMin ?? "",
        userTradeVolumeFilterTime: adv.userTradeVolumeFilterTime ?? 0,
        userTradeType: adv.userTradeType ?? 0,
        userTradeVolumeMin: adv.userTradeVolumeMin ?? "",
        userTradeVolumeMax: adv.userTradeVolumeMax ?? "",
        userTradeVolumeAsset: adv.userTradeVolumeAsset ?? null,

        // Country
        launchCountry: adv.launchCountry ?? null,

        // Trade method commissions
        tradeMethodCommissionRates: (adv.tradeMethodCommissionRates || []).map(
          (r: any) => ({
            tradeMethodIdentifier: r.tradeMethodIdentifier ?? "",
            tradeMethodName: r.tradeMethodName ?? "",
            commissionRate: r.commissionRate ?? "",
          }),
        ),

        // Required by P2PAd (nested inside P2PAdDetail.adv)
        advertiser: {
          userNo: advertiser.userNo ?? "",
          nickName: advertiser.nickName ?? "",
          userType: normalizeUserType(advertiser),
          monthFinishRate: Number(advertiser.monthFinishRate ?? 0),
          monthOrderCount: Number(advertiser.monthOrderCount ?? 0),
          positiveRate: Number(advertiser.positiveRate ?? 0),
          activeTimeInSecond: Number(advertiser.activeTimeInSecond ?? 9999),
          vipLevel: Number(advertiser.vipLevel ?? 0),
        },

        // Optional fields
        assetScale: adv.assetScale,
        buyerKycLimit: adv.buyerKycLimit,
      },
      advertiser: {
        userNo: advertiser.userNo ?? "",
        nickName: advertiser.nickName ?? "",
        userType: normalizeUserType(advertiser),
        monthFinishRate: Number(advertiser.monthFinishRate ?? 0),
        monthOrderCount: Number(advertiser.monthOrderCount ?? 0),
        positiveRate: Number(advertiser.positiveRate ?? 0),
        activeTimeInSecond: Number(advertiser.activeTimeInSecond ?? 9999),
        vipLevel: Number(advertiser.vipLevel ?? 0),
        orderCount: advertiser.orderCount,

        // Binance-specific advertiser fields
        userStatsRet: {
          registerDays: advertiser.registerDays ?? 0,
          firstOrderDays: advertiser.firstOrderDays ?? 0,
          avgReleaseTimeOfLatest30day:
            advertiser.avgReleaseTimeOfLatest30day ?? 0,
          avgPayTimeOfLatest30day: advertiser.avgPayTimeOfLatest30day ?? 0,
          finishRateLatest30day: Number(advertiser.finishRateLatest30day ?? 0),
          completedOrderNumOfLatest30day:
            advertiser.completedOrderNumOfLatest30day ?? 0,
          completedBuyOrderNumOfLatest30day:
            advertiser.completedBuyOrderNumOfLatest30day ?? 0,
          completedSellOrderNumOfLatest30day:
            advertiser.completedSellOrderNumOfLatest30day ?? 0,
          completedOrderTotalBtcAmountOfLatest30day:
            advertiser.completedOrderTotalBtcAmountOfLatest30day ?? 0,
          completedOrderNum: advertiser.completedOrderNum ?? 0,
          completedBuyOrderNum: advertiser.completedBuyOrderNum ?? 0,
          completedSellOrderNum: advertiser.completedSellOrderNum ?? 0,
          completedBuyOrderTotalBtcAmount:
            advertiser.completedBuyOrderTotalBtcAmount ?? 0,
          completedSellOrderTotalBtcAmount:
            advertiser.completedSellOrderTotalBtcAmount ?? 0,
          completedOrderTotalBtcAmount:
            advertiser.completedOrderTotalBtcAmount ?? 0,
          counterpartyCount: advertiser.counterpartyCount ?? 0,
        },
        userKycRet: advertiser.userKycRet,
        businessStatus: advertiser.businessStatus ?? null,
        overComplained: advertiser.overComplained ?? null,
        onlineStatus: advertiser.onlineStatus ?? null,
        registerDays: advertiser.registerDays ?? null,
        firstOrderDays: advertiser.firstOrderDays ?? null,
        chanelInvolved: advertiser.chanelInvolved ?? "",
        userIdentity: advertiser.userIdentity ?? "",
        userGradeInstanceRet: advertiser.userGradeInstanceRet ?? null,
        proMerchant: advertiser.proMerchant ?? null,
        badges: advertiser.badges || [],
        tradeOrderCount: advertiser.tradeOrderCount ?? null,
        isBlocked: advertiser.isBlocked ?? null,
        alertEnabled: advertiser.alertEnabled ?? null,
        lastActiveTime: advertiser.lastActiveTime ?? null,
        companyAccountName: advertiser.companyAccountName ?? "",
        isCompanyAccount: advertiser.isCompanyAccount ?? false,
        eqMargin: advertiser.eqMargin ?? 0,
        eqMarginUnit: advertiser.eqMarginUnit ?? "",
        marginStats: advertiser.marginStats || [],
        avatar: advertiser.avatar ?? null,
        margin: advertiser.margin ?? 0,
        marginUnit: advertiser.marginUnit ?? "",
        decimalScale: advertiser.decimalScale ?? 0,
        advConfirmTime: advertiser.advConfirmTime ?? 0,
        emailVerified: advertiser.emailVerified ?? false,
        bindMobile: advertiser.bindMobile ?? false,
        kycVerified: advertiser.kycVerified ?? false,
        registrationTime: advertiser.registrationTime ?? null,
        payMethodCount: advertiser.payMethodCount ?? null,
        mobile: advertiser.mobile ?? null,
        kycType: advertiser.kycType ?? 0,
        kycStatus: advertiser.kycStatus ?? "",
        userGrade: advertiser.userGrade ?? 0,
      },
    };
  },

  // ─── isFakeAd (Binance heuristics) ────────────────────────────────
  isFakeAd: (ad: P2PAd) => isFakeAds(ad),

  // ─── getVipStyle (Binance nickname rules) ─────────────────────────
  getVipStyle: (ad: P2PAd, accessLevel: number) =>
    getPromotionZoneStyle(ad, accessLevel),

  // ─── getPriceSignals (Binance emoji indicators) ───────────────────
  getPriceSignals: (ad: P2PAd, accessLevel: number, ctx) =>
    getPriceSignals(ad, accessLevel, ctx),

  // ─── getPayTimeOverride (Binance fast BUY / slow SELL nicknames) ──
  getPayTimeOverride: (ad: P2PAd, accessLevel: number) => {
    if (accessLevel < 1) return null;
    const nick = ad.advertiser.nickName;
    const fastBuyNicknames = [
      "GiaoDichVIP_247",
      "TổngKhoUSDT_247",
      "BendTheTrend x HỏaTốc247",
      "ThuongGiaTienSo_VIP",
      "HoaToc247",
      "VIP-GiaoDichNhanh",
    ];
    if (
      fastBuyNicknames.some((k) => nick.includes(k)) &&
      ad.payTimeLimit === 15 &&
      ad.tradeType === "BUY"
    ) {
      return "opacity-60 underline !text-yellow-400";
    }
    const slowSellNicknames = [
      "GiaoDichVIP_247",
      "TổngKhoUSDT_247",
      "BendTheTrend x HỏaTốc247",
      "ThuongGiaTienSo_VIP",
      "HoaToc247",
      "VIP-GiaoDichNhanh",
    ];
    if (
      slowSellNicknames.some((k) => nick.includes(k)) &&
      ad.payTimeLimit === 30 &&
      ad.tradeType === "SELL"
    ) {
      return "opacity-50 underline !text-yellow-400";
    }
    return null;
  },

  // ─── getMerchantColorRules (Binance default behavior) ─────────────
  getMerchantColorRules: (ad: P2PAd) => {
    // Cyan for merchants, white/gray for users — this is Binance's default
    return ad.advertiser.userType === "merchant" ? "text-cyan-300" : null;
  },

  // ─── isMerchantPerspective (Binance: customer perspective) ────────
  isMerchantPerspective: () => false,

  // ─── getLogo (Binance default) ──────────────────────────────────
  getLogo: (ad: P2PAd) => (ad.advertiser.userType === "merchant" ? "👑" : "🐮"),

  // ─── Binance P2P links ───────────────────────────────────────────
  getAdvertiserLink: (userNo: string) =>
    `https://p2p.binance.com/en/advertiserDetail?advertiserNo=${userNo}`,
  getLink: (advNo: string) => `https://p2p.binance.com/en/adv?code=${advNo}`,
};
